import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  useTheme,
} from "@material-ui/core";
import { AttachFile, Close, InsertDriveFile } from "@material-ui/icons";
import { useField, useFormikContext } from "formik";
import { useCallback, useEffect, useId, useRef, useState } from "react";

function formatBytes(bytes) {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 || value >= 10 ? 0 : 1)} ${
    units[exponent]
  }`;
}

function isImageValue(file) {
  const mime = file?.mimeType || file?.type || "";
  if (mime.startsWith("image/")) {
    return true;
  }
  const candidates = [file?.name, file?.url].filter(Boolean);
  return candidates.some((candidate) =>
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(candidate)
  );
}

function acceptMatches(accept, file) {
  if (!accept) return true;
  const extension = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
  return accept
    .split(",")
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean)
    .some((type) => {
      if (type.startsWith(".")) return type === extension;
      if (type.endsWith("/*")) return file.type.startsWith(type.slice(0, -1));
      return file.type.toLowerCase() === type;
    });
}

export function UploadField({
  label,
  disabled,
  helperText,
  accept,
  maxSize,
  margin = "normal",
  variant = "dropzone",
  multiple = false,
  onFileSelect,
  onPreview,
  ...props
}) {
  const theme = useTheme();
  const { isSubmitting } = useFormikContext();
  const [field, meta, helpers] = useField(props);
  const inputId = useId();
  const inputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const isPending = disabled || isSubmitting;
  const hasError = !!(meta.touched && meta.error);
  const file = field.value;
  const isDropzone = variant === "dropzone";
  const selectedFiles = multiple
    ? Array.isArray(field.value)
      ? field.value
      : []
    : [];

  useEffect(() => {
    if (
      !multiple &&
      isDropzone &&
      file instanceof File &&
      file.type.startsWith("image/")
    ) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setPreviewUrl(null);
  }, [file, isDropzone, multiple]);

  useEffect(() => {
    setImgFailed(false);
  }, [file]);

  const validateFile = useCallback(
    (nextFile) => {
      if (accept && !acceptMatches(accept, nextFile)) {
        return `Only ${accept} are allowed`;
      }
      if (maxSize && nextFile.size > maxSize) {
        return `File is too large. Maximum size is ${formatBytes(maxSize)}`;
      }
      return null;
    },
    [accept, maxSize]
  );

  const setFile = useCallback(
    (nextFile) => {
      if (!nextFile) return;
      if (onFileSelect) {
        onFileSelect(nextFile);
        return;
      }
      const error = validateFile(nextFile);
      if (error) {
        helpers.setError(error);
        helpers.setTouched(true, false);
        return;
      }
      helpers.setError(undefined);
      helpers.setValue(nextFile, true);
    },
    [helpers, onFileSelect, validateFile]
  );

  const setFiles = useCallback(
    (nextFiles) => {
      const incoming = nextFiles.filter(Boolean);
      if (!incoming.length) return;
      if (onFileSelect) {
        incoming.forEach(onFileSelect);
        return;
      }
      const rejected = [];
      const valid = [];
      incoming.forEach((nextFile) => {
        const error = validateFile(nextFile);
        if (error) {
          rejected.push(`${nextFile.name}: ${error}`);
        } else {
          valid.push(nextFile);
        }
      });
      if (rejected.length) {
        helpers.setError(rejected.join(", "));
        helpers.setTouched(true, false);
      } else {
        helpers.setError(undefined);
      }
      if (valid.length) {
        helpers.setValue([...selectedFiles, ...valid], true);
      }
    },
    [helpers, onFileSelect, selectedFiles, validateFile]
  );

  const handleChange = useCallback(
    (event) => {
      const nextFiles = Array.from(event.target.files || []);
      if (multiple) {
        setFiles(nextFiles);
      } else {
        setFile(nextFiles[0]);
      }
      event.target.value = "";
    },
    [multiple, setFile, setFiles]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setIsDragActive(false);
      const nextFiles = Array.from(event.dataTransfer.files || []);
      if (multiple) {
        setFiles(nextFiles);
      } else {
        setFile(nextFiles[0]);
      }
    },
    [multiple, setFile, setFiles]
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setIsDragActive(false);
  }, []);

  const openPicker = useCallback(() => {
    if (!isPending) inputRef.current?.click();
  }, [isPending]);

  const handleClear = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      if (inputRef.current) inputRef.current.value = "";
      helpers.setError(undefined);
      helpers.setValue(multiple ? [] : undefined, true);
    },
    [helpers, multiple]
  );

  const handleRemoveFile = useCallback(
    (index, event) => {
      event?.stopPropagation();
      event?.preventDefault();
      if (inputRef.current) inputRef.current.value = "";
      helpers.setError(undefined);
      helpers.setValue(
        selectedFiles.filter((_, i) => i !== index),
        true
      );
    },
    [helpers, selectedFiles]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openPicker();
      }
    },
    [openPicker]
  );

  const fileName =
    file?.name || (file?.url && file?.url.split("/").pop()) || "";
  const isExistingImage =
    !multiple &&
    isDropzone &&
    file &&
    !(file instanceof File) &&
    isImageValue(file) &&
    typeof file?.url === "string" &&
    /^(https?:|blob:|data:)/.test(file.url);
  const borderColor = hasError
    ? theme.palette.error.main
    : isDragActive
    ? theme.palette.primary.main
    : theme.palette.grey[400];

  console.log(
    "[UploadField] file:",
    file,
    "| isExistingImage:",
    isExistingImage,
    "| imgFailed:",
    imgFailed,
    "| previewUrl:",
    previewUrl
  );

  return (
    <FormControl fullWidth margin={margin} className={props.className}>
      {label && (
        <InputLabel htmlFor={inputId} shrink error={hasError}>
          {label}
        </InputLabel>
      )}
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={isPending}
        style={{ display: "none" }}
      />
      {isDropzone ? (
        <Box
          mt={label ? 1 : 0}
          role="button"
          tabIndex={isPending ? -1 : 0}
          onClick={openPicker}
          onKeyDown={handleKeyDown}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          aria-label={label}
          border={1}
          borderStyle="dashed"
          borderRadius={2}
          p={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={80}
          style={{
            borderColor,
            backgroundColor: isDragActive
              ? theme.palette.action.hover
              : undefined,
            cursor: isPending ? "default" : "pointer",
            outline: "none",
            transition: "border-color 0.15s ease-in-out",
            marginTop: label ? 24 : 0,
          }}
        >
          {!multiple && file ? (
            <Box display="flex" alignItems="center" width="100%">
              {(previewUrl || isExistingImage) && !imgFailed ? (
                <Box
                  component="img"
                  src={previewUrl || file.url}
                  alt={fileName}
                  width={44}
                  height={44}
                  borderRadius={1}
                  onError={() => setImgFailed(true)}
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <InsertDriveFile color="action" />
              )}
              <Box
                ml={1}
                flexGrow={1}
                minWidth={0}
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Box fontSize="body1.fontSize">{fileName}</Box>
                {file.size != null && (
                  <Box color="text.secondary" fontSize="caption.fontSize">
                    {formatBytes(file.size)}
                  </Box>
                )}
              </Box>
              <IconButton
                size="small"
                disabled={isPending}
                onClick={handleClear}
                onMouseDown={(event) => event.stopPropagation()}
                aria-label="Remove file"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              flexDirection="column"
              color="text.secondary"
            >
              <AttachFile />
              <Box fontSize="body2.fontSize">
                {multiple && selectedFiles.length
                  ? `${selectedFiles.length} file(s) selected`
                  : "Click or drag a file here"}
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          mt={label ? 1 : 0}
          display="flex"
          alignItems="center"
          p={1}
          bgcolor={theme.palette.grey[50]}
          border={1}
          borderRadius="6px"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            gap: theme.spacing(1),
            borderColor: isFocused
              ? theme.palette.primary.main
              : theme.palette.grey[300],
            boxShadow: isFocused
              ? `0 0 0 1px ${theme.palette.primary.main}`
              : undefined,
            transition:
              "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
            marginTop: label ? 24 : 0,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<AttachFile style={{ fontSize: 14 }} />}
            onClick={openPicker}
            disabled={isPending}
            style={{
              textTransform: "none",
              fontSize: 12,
              borderColor: theme.palette.grey[300],
              backgroundColor: theme.palette.background.paper,
            }}
          >
            Upload File
          </Button>
          <Box
            component="span"
            color="text.secondary"
            flexGrow={1}
            minWidth={0}
            style={{
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {!multiple && file
              ? fileName
              : multiple && selectedFiles.length
              ? `${selectedFiles.length} file(s) selected`
              : "Pilih dokumen (PDF, PNG, JPG)"}
          </Box>
          {!multiple && file && (
            <IconButton
              size="small"
              onClick={handleClear}
              onMouseDown={(event) => event.stopPropagation()}
              disabled={isPending}
              aria-label="Remove file"
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
      {multiple && selectedFiles.length > 0 && (
        <Box mt={1} display="flex" flexDirection="column" style={{ gap: 4 }}>
          {selectedFiles.map((selectedFile, index) => (
            <Box
              key={
                selectedFile.id ||
                selectedFile._id ||
                `${selectedFile.name}-${index}`
              }
              display="flex"
              alignItems="center"
              px={1}
              py={0.5}
              borderRadius={1}
              bgcolor={theme.palette.grey[100]}
              style={{ gap: theme.spacing(1) }}
            >
              <InsertDriveFile color="action" fontSize="small" />
              <Box
                flexGrow={1}
                minWidth={0}
                style={{
                  fontSize: 12,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedFile.name || selectedFile.url || ""}
                {selectedFile.size != null && (
                  <Box component="span" color="text.secondary">
                    {" "}
                    - {formatBytes(selectedFile.size)}
                  </Box>
                )}
              </Box>
              {onPreview && selectedFile.url && (
                <Button size="small" onClick={() => onPreview(selectedFile)}>
                  Preview
                </Button>
              )}
              <IconButton
                size="small"
                disabled={isPending}
                onClick={(event) => handleRemoveFile(index, event)}
                onMouseDown={(event) => event.stopPropagation()}
                aria-label="Remove file"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
      {hasError ? (
        <FormHelperText error>{meta.error}</FormHelperText>
      ) : helperText ? (
        <FormHelperText>{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
